import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOverduePayments,
  updateOverduePayments
} from '../../../Redux/Actions/actions';
import {
  FaExclamationTriangle,
  FaDollarSign,
  FaCalendarAlt,
  FaUser
} from 'react-icons/fa';
import RegisterPaymentModal from '../../../Components/RegisterPaymentModal/RegisterPaymentModal';
import { devLog } from '../../../utils/devLog';
import {
  RENTAL_TYPE,
  formatCurrency,
  formatDate,
  getDetailPath
} from '../utils/rentalTypeHelpers';
import style from './OverduePayments.module.css';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const OverduePayments = () => {
  const dispatch = useDispatch();
  const {
    overduePayments,
    rentalsLoading,
    rentalsError
  } = useSelector((state) => state);

  const [typeFilter, setTypeFilter] = useState('all');
  const [activePayment, setActivePayment] = useState(null);

  const refresh = async () => {
    try {
      await dispatch(updateOverduePayments());
    } catch (err) {
      console.error(
        'No se pudo actualizar mora antes de consultar vencidos:',
        err
      );
    } finally {
      dispatch(fetchOverduePayments());
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    devLog('OverduePayments/overduePayments', {
      count: overduePayments?.length ?? 0,
      loading: rentalsLoading,
      error: rentalsError
    });
  }, [overduePayments, rentalsLoading, rentalsError]);

  const filtered = useMemo(() => {
    if (typeFilter === 'all') return overduePayments || [];
    return (overduePayments || []).filter(
      (payment) => payment.rentalKind === typeFilter
    );
  }, [overduePayments, typeFilter]);

  const totalOverdue = useMemo(
    () => filtered.reduce((acc, p) => acc + toNumber(p.totalAmount), 0),
    [filtered]
  );

  const calculateOverdueDays = (item) => {
    if (item?.daysOverdueCalculated != null) {
      return toNumber(item.daysOverdueCalculated);
    }
    if (item?.daysOverdue != null && toNumber(item.daysOverdue) > 0) {
      return toNumber(item.daysOverdue);
    }
    const today = new Date();
    const due = new Date(item.dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const isLoading = rentalsLoading;
  const errorMessage = rentalsError;

  if (isLoading && (!overduePayments || overduePayments.length === 0)) {
    return <div className={style.loading}>Cargando pagos vencidos...</div>;
  }
  if (errorMessage) {
    return <div className={style.error}>Error: {errorMessage}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaExclamationTriangle className={style.titleIcon} />
          Pagos Vencidos
        </h1>

        <div className={style.summary}>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Total Pagos Vencidos:</span>
            <span className={style.summaryValue}>{filtered.length}</span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Monto Total Adeudado:</span>
            <span className={`${style.summaryValue} ${style.amount}`}>
              {formatCurrency(totalOverdue)}
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: '12px 0',
          flexWrap: 'wrap'
        }}
      >
        {[
          { v: 'all', l: 'Todos' },
          { v: RENTAL_TYPE.REGULAR, l: 'Regular' },
          { v: RENTAL_TYPE.ADMIN, l: 'Solo Admin' }
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setTypeFilter(opt.v)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: typeFilter === opt.v ? '#1e40af' : '#e5e7eb',
              color: typeFilter === opt.v ? '#fff' : '#374151',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className={style.noData}>
          <FaExclamationTriangle className={style.noDataIcon} />
          <h3>¡Excelente!</h3>
          <p>No hay pagos vencidos en este momento</p>
        </div>
      ) : (
        <div className={style.paymentsList}>
          {filtered.map((item) => {
            const overdueDays = calculateOverdueDays(item);
            const isHighPriority = overdueDays > 30;
            const isAdmin = item.rentalKind === RENTAL_TYPE.ADMIN;
            const tenantPhone = item.tenant?.phone;
            const tenantName = item.tenant?.fullName || 'No asignado';
            const lateInterest = item.estimatedLateInterest;

            return (
              <div
                key={`${item.rentalKind}-${item.id}`}
                className={`${style.paymentCard} ${
                  isHighPriority ? style.highPriority : ''
                }`}
              >
                <div className={style.cardHeader}>
                  <div className={style.propertyInfo}>
                    <h3 className={style.propertyTitle}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          background: isAdmin ? '#fef3c7' : '#dbeafe',
                          color: isAdmin ? '#92400e' : '#1e40af',
                          marginRight: 8,
                          verticalAlign: 'middle'
                        }}
                      >
                        {isAdmin ? 'Admin' : 'Regular'}
                      </span>
                      {item.property?.address || 'Propiedad sin dirección'}
                    </h3>
                    <p className={style.propertyAddress}>
                      Propietario: {item.ownerName || '—'}
                    </p>
                  </div>

                  <div
                    className={`${style.priorityBadge} ${
                      isHighPriority ? style.high : style.medium
                    }`}
                  >
                    {isHighPriority ? 'Prioridad Alta' : 'Prioridad Media'}
                  </div>
                </div>

                <div className={style.cardContent}>
                  <div className={style.paymentInfo}>
                    <div className={style.infoItem}>
                      <FaUser className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>Inquilino</span>
                        <span className={style.infoValue}>{tenantName}</span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <FaCalendarAlt className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>
                          Fecha Vencimiento
                        </span>
                        <span className={style.infoValue}>
                          {formatDate(item.dueDate)}
                        </span>
                        <span className={style.overdueDays}>
                          ({overdueDays} días de atraso)
                        </span>
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <FaDollarSign className={style.infoIcon} />
                      <div>
                        <span className={style.infoLabel}>Monto Adeudado</span>
                        <span
                          className={`${style.infoValue} ${style.amount}`}
                        >
                          {formatCurrency(item.totalAmount)}
                        </span>
                        {toNumber(lateInterest) > 0 && (
                          <span className={style.lateInterest}>
                            + {formatCurrency(lateInterest)} interés
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={style.infoItem}>
                      <span className={style.infoLabel}>Período</span>
                      <span className={style.infoValue}>
                        {formatDate(item.periodStart)} -{' '}
                        {formatDate(item.periodEnd)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={style.cardActions}>
                  <button
                    className={`${style.actionButton} ${style.primary}`}
                    onClick={() => setActivePayment(item)}
                  >
                    Registrar Pago
                  </button>
                  {tenantPhone && (
                    <button
                      className={`${style.actionButton} ${style.secondary}`}
                      onClick={() =>
                        window.open(`tel:${tenantPhone}`)
                      }
                    >
                      Contactar Inquilino
                    </button>
                  )}
                  {item.rentalId && (
                    <Link
                      to={getDetailPath(item.rentalId, item.rentalKind)}
                      className={`${style.actionButton} ${style.secondary}`}
                    >
                      Ver Contrato
                    </Link>
                  )}
                  {item.rentalId && (
                    <Link
                      to={getDetailPath(
                        item.rentalId,
                        item.rentalKind,
                        'pagos'
                      )}
                      className={`${style.actionButton} ${style.secondary}`}
                    >
                      <FaDollarSign /> Pagos
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activePayment && (
        <RegisterPaymentModal
          paymentId={activePayment.id}
          paymentInfo={activePayment}
          type={activePayment.rentalKind}
          onClose={() => setActivePayment(null)}
          onRegistered={() => {
            setActivePayment(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default OverduePayments;
